import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { clsx } from 'clsx';
import type { TradeItem, BulkReRateEntry, BulkReRateFailure } from '@/types';
import { Button } from '@/components/ui/Button';
import { DecimalInput } from '@/components/ui/DecimalInput';
import { useCurrencyDisplayStore } from '@/stores';
import { getErrorMessage } from '@/utils/get-error-message';
import { formatDecimalString } from '@/utils/decimal-display';
import { formatCurrencyExact } from '@/utils/trades/mtm-calculations';
import { aggregateByCurrency } from '@/utils/trades/currency-totals';
import {
  benchmarkFollowsFixing,
  breachesFeeFloor,
  LOW_RATE_WARN_PCT,
  reRateEditableFields,
  reRateMode,
  warnsLowResultingRate,
} from '@/services/trades/rerate-mapper';
import { effectiveTradeRates } from '@/services/trades/effective-rate';
import { useBulkReRate } from '@/hooks/trades/use-bulk-rerate';
import { useBulkReRateStore } from '@/stores/trades/bulk-rerate-store';
import {
  bulkReRateIneligibleReason,
  buildBulkEntry,
  computeNewTotalRate,
  resolveRowValues,
  rowDailyDelta,
  type BulkRateOverride,
} from '@/features/trades/utils/bulk-rerate-calculations';

const { Panel: DialogPanel, Title: DialogTitle } = Dialog;
const { Child: TransitionChild } = Transition;

interface BulkReRateModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** The selected trades to stage (the blotter selection). */
  trades: TradeItem[];
  /** Called once the batch is applied (success only) — clears the blotter
   *  selection so the bulk bar doesn't linger over just-re-rated rows and a
   *  repeat future-dated batch can't hit the R4 already-pending 400. */
  onApplied?: () => void;
}

/** Effective-date window is validated by the backend in UTC — match it so the
 *  picker min and the today/future check stay in lockstep (mirrors ReRateModal). */
const todayUtcKey = (): string => new Date().toISOString().slice(0, 10);

/** Mirror BulkReRateSerializer.BATCH_CAP — reject over-cap client-side so the
 *  user gets a hint instead of a silent backend 400. */
const BATCH_CAP = 200;

const rateStr = (r: number | undefined | null): string =>
  r === undefined || r === null || !Number.isFinite(r) ? '—' : `${formatDecimalString(r, { dp: 2 })}%`;

const signed = (n: number): string => (n > 0 ? `+${formatDecimalString(n, { dp: 2 })}` : formatDecimalString(n, { dp: 2 }));

const TH = 'px-2 py-1.5 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500 whitespace-nowrap';
const TD = 'px-2 py-1.5 text-sm text-gray-800 whitespace-nowrap';

export const BulkReRateModal: React.FC<BulkReRateModalProps> = ({ isOpen, onClose, onApplied, trades }) => {
  const baseCurrency = useCurrencyDisplayStore((s) => s.baseCurrency);
  const bulkReRate = useBulkReRate();

  const {
    overrides, excludedIds, mode, adjustBps, effectiveDate, reason,
    setField, toggleExclude, setMode, setAdjustBps, setEffectiveDate, setReason, reset,
  } = useBulkReRateStore();

  const [step, setStep] = useState<'staging' | 'confirm'>('staging');

  const ineligibleById = useMemo(() => {
    const m = new Map<number, string>();
    for (const t of trades) {
      const r = bulkReRateIneligibleReason(t);
      if (r) m.set(t.id, r);
    }
    return m;
  }, [trades]);

  // On open: seed the store (today + auto-exclude every ineligible-by-status row).
  useEffect(() => {
    if (!isOpen) return;
    reset({ effectiveDate: todayUtcKey(), excludedIds: [...ineligibleById.keys()] });
    setStep('staging');
    bulkReRate.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const included = useMemo(
    () => trades.filter((t) => !excludedIds.has(t.id)),
    [trades, excludedIds],
  );

  // Resolve each INCLUDED row ONCE → wire entry (drops no-op rows) plus the
  // fee-floor tally. One source of truth for the grid summary and the POST body;
  // one pass, because BulkRow is memoised precisely so a keystroke in Adjust-by-bps
  // does not re-resolve all 200 rows — a second full traversal here would undo that.
  const { entries, feeFloorCount, lowRateCount } = useMemo(() => {
    const built: BulkReRateEntry[] = [];
    let feeFloor = 0;
    let lowRate = 0;
    for (const t of included) {
      const resolved = resolveRowValues(t, mode, adjustBps, overrides[t.id]);
      if (breachesFeeFloor(t, resolved)) feeFloor += 1;
      const entry = buildBulkEntry(t, resolved);
      if (entry !== null) {
        built.push(entry);
        // Only rows that actually MOVE can be fat-fingered here: a book already
        // sitting below the threshold must not open the batch pre-warned about a
        // rate nobody touched (same discipline as ReRateModal's hasChange gate).
        if (warnsLowResultingRate(t, computeNewTotalRate(t, resolved))) lowRate += 1;
      }
    }
    return { entries: built, feeFloorCount: feeFloor, lowRateCount: lowRate };
  }, [included, mode, adjustBps, overrides]);

  // ≈Σ Δ daily P&L rolled up to the base currency (RUB / no-FX rows drop out of
  // the equiv, surfaced via the aggregate's approx flags — same as the blotter footer).
  const deltaAgg = useMemo(() => {
    const changedIds = new Set(entries.map((e) => e.position_id));
    const rows = included.filter((t) => changedIds.has(t.id));
    return aggregateByCurrency(
      rows,
      (t) => rowDailyDelta(t, computeNewTotalRate(t, resolveRowValues(t, mode, adjustBps, overrides[t.id]))) ?? 0,
      baseCurrency,
      (t) => t.currency,
    );
  }, [entries, included, mode, adjustBps, overrides, baseCurrency]);

  const failed: BulkReRateFailure[] = useMemo(() => {
    const err = bulkReRate.error as
      | (Error & { response?: { data?: { failed?: BulkReRateFailure[] } }; data?: { failed?: BulkReRateFailure[] } })
      | null;
    return err?.response?.data?.failed ?? err?.data?.failed ?? [];
  }, [bulkReRate.error]);

  const adjustReady = mode === 'set_per_row' || (adjustBps !== null && adjustBps !== 0);
  // Bulk is today/future only. The <input min> is spinner-only — a typed past
  // date bypasses it and the backend 400s on effective_date (invisible without
  // the generic error band below), so re-check the range here.
  const dateOk = !!effectiveDate && effectiveDate >= todayUtcKey();
  const withinCap = entries.length <= BATCH_CAP;
  // One derivation of "why not", so the staging footer, the Confirm button and the
  // confirm step cannot disagree — and a button that dies under a background
  // refetch says what killed it instead of just greying out.
  // Order matters: an empty adjustment yields no entries at all, so testing
  // `entries.length` first would make the adjustment branch dead and report the
  // less useful "nothing changed" instead of the thing to actually do.
  const blockReason =
    !adjustReady ? 'enter a non-zero bps adjustment'
    : entries.length === 0 ? 'no rows have changed'
    : !withinCap ? `batch is over the ${BATCH_CAP}-row cap`
    : !dateOk ? 'effective date must be today or later'
    : feeFloorCount > 0 ? `${feeFloorCount} row(s) would take fee to 0% or below`
    : null;
  const canApply = blockReason === null && !bulkReRate.isPending;

  const handleConfirm = () => {
    // The staging controls are unmounted on this step, but `trades` is a live prop —
    // a background refetch can move a row under the confirm screen after the gate
    // passed. Re-check, the same submit-path discipline ReRateModal uses.
    if (!canApply) return;
    bulkReRate.mutate(
      { entries, effective_date: effectiveDate, reason: reason.trim() || undefined },
      { onSuccess: () => { onApplied?.(); onClose(); } },
    );
  };

  // A shared, uniform dimension across the included rows lives in the title.
  const uniform = <K extends keyof TradeItem>(key: K): TradeItem[K] | null => {
    if (included.length === 0) return null;
    const first = included[0][key];
    return included.every((t) => t[key] === first) ? first : null;
  };
  const titleCp = uniform('counterparty');
  const titleSym = uniform('symbol_id');

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[70]" onClose={bulkReRate.isPending ? () => {} : onClose}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100"
          leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40" />
        </TransitionChild>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-200" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100"
              leave="ease-in duration-150" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95"
            >
              <DialogPanel className="w-full max-w-4xl overflow-hidden rounded-xl bg-white shadow-2xl">
                {/* Header */}
                <div className="flex items-center justify-between border-b border-gray-200 px-5 py-3">
                  <DialogTitle className="text-base font-semibold text-gray-900">
                    Re-rate {included.length} {included.length === 1 ? 'trade' : 'trades'}
                    {titleSym ? ` · ${titleSym}` : ''}
                    {titleCp ? ` · ${titleCp}` : ''}
                  </DialogTitle>
                  <button
                    type="button" onClick={onClose} aria-label="Close"
                    className="p-1 text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                {step === 'staging' ? (
                  <>
                    {/* Batch input controls */}
                    <div className="flex flex-wrap items-end gap-4 border-b border-gray-100 bg-gray-50 px-5 py-3">
                      <div>
                        <label className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-gray-500">
                          New rate
                        </label>
                        <div className="inline-flex overflow-hidden rounded-md border border-gray-300">
                          {(['set_per_row', 'adjust_bps'] as const).map((m) => (
                            <button
                              key={m} type="button" onClick={() => setMode(m)}
                              aria-pressed={mode === m}
                              className={clsx(
                                'px-3 py-1.5 text-sm',
                                mode === m ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50',
                              )}
                            >
                              {m === 'set_per_row' ? 'Set per row' : 'Adjust by bps'}
                            </button>
                          ))}
                        </div>
                      </div>

                      {mode === 'adjust_bps' && (
                        <div>
                          <label className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-gray-500">
                            ± bps (applied to each row)
                          </label>
                          <DecimalInput
                            value={adjustBps ?? undefined}
                            onChange={(v) => setAdjustBps(v ?? null)}
                            precision={0}
                            allowNegative
                            aria-label="Adjust by bps (applied to each row)"
                            className="w-28 rounded border border-gray-300 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none"
                            placeholder="e.g. 25 or -25"
                          />
                        </div>
                      )}

                      <div>
                        <label className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-gray-500">
                          Effective date
                        </label>
                        <input
                          type="date" value={effectiveDate} min={todayUtcKey()}
                          aria-label="Effective date"
                          onChange={(e) => setEffectiveDate(e.target.value)}
                          className="rounded border border-gray-300 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none"
                        />
                      </div>

                      <div className="flex-1 min-w-[160px]">
                        <label className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-gray-500">
                          Reason (optional)
                        </label>
                        <input
                          type="text" value={reason} maxLength={150}
                          aria-label="Reason (optional)"
                          onChange={(e) => setReason(e.target.value)}
                          className="w-full rounded border border-gray-300 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none"
                          placeholder="e.g. GC repricing July"
                        />
                      </div>
                    </div>

                    {/* Staging grid */}
                    <div className="max-h-[52vh] overflow-auto">
                      <table className="w-full border-collapse">
                        <thead className="sticky top-0 bg-white shadow-[0_1px_0_#e5e7eb]">
                          <tr>
                            <th className={TH}></th>
                            <th className={TH}>ID</th>
                            <th className={TH}>Symbol</th>
                            <th className={TH}>Counterparty</th>
                            <th className={TH}>Current</th>
                            <th className={TH}>New value</th>
                            <th className={clsx(TH, 'text-right')}>Δ rate</th>
                            <th className={clsx(TH, 'text-right')}>Δ daily P&amp;L</th>
                          </tr>
                        </thead>
                        <tbody>
                          {trades.map((t) => (
                            <BulkRow
                              key={t.id}
                              trade={t}
                              ineligibleReason={ineligibleById.get(t.id) ?? null}
                              excluded={excludedIds.has(t.id)}
                              mode={mode}
                              adjustBps={adjustBps}
                              override={overrides[t.id]}
                              onToggle={toggleExclude}
                              onField={setField}
                            />
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-between border-t border-gray-200 px-5 py-3">
                      <div className="text-sm text-gray-600">
                        <span className="font-semibold text-gray-900">{entries.length}</span> of {included.length} changed
                        {entries.length > BATCH_CAP && (
                          <span className="ml-3 text-amber-600" role="alert">
                            Max {BATCH_CAP} per batch — narrow the selection.
                          </span>
                        )}
                        {!dateOk && (
                          <span className="ml-3 text-amber-600" role="alert">
                            Effective date must be today or later.
                          </span>
                        )}
                        {/* Guidance, not an error: it appears from switching mode, so
                            role="status" — unlike the date, which is a typed mistake. */}
                        {!adjustReady && (
                          <span className="ml-3 text-amber-600" role="status">
                            Enter a non-zero bps adjustment.
                          </span>
                        )}
                        {feeFloorCount > 0 && (
                          <span className="ml-3 text-amber-600" role="alert">
                            {feeFloorCount} row(s) marked “fee ≤ 0” — fee must stay above 0%.
                            Make the change smaller or exclude those rows.
                          </span>
                        )}
                        {lowRateCount > 0 && (
                          <span className="ml-3 text-yellow-700" role="status">
                            {lowRateCount} row(s) marked “rate &lt; {LOW_RATE_WARN_PCT}%” — verify
                            before applying.
                          </span>
                        )}
                        {entries.length > 0 && (
                          <span className="ml-3">
                            ≈Σ Δ daily{' '}
                            <span className={clsx('font-mono font-semibold', deltaAgg.equiv >= 0 ? 'text-emerald-600' : 'text-rose-600')}>
                              {signed(deltaAgg.equiv)} {deltaAgg.base}
                            </span>
                            {deltaAgg.approxCurrencies.length > 0 && (
                              <span className="ml-1 text-amber-600" title={`No FX for ${deltaAgg.approxCurrencies.join(', ')} — excluded from Σ`}>⚠</span>
                            )}
                          </span>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button variant="secondary" onClick={onClose}>Cancel</Button>
                        <Button
                          variant="primary"
                          disabled={!canApply}
                          title={blockReason ?? undefined}
                          onClick={() => setStep('confirm')}
                        >
                          Re-rate {entries.length}…
                        </Button>
                      </div>
                    </div>
                  </>
                ) : (
                  /* Confirm step */
                  <div className="px-5 py-4">
                    <p className="text-sm text-gray-700">
                      Apply a rate change to <span className="font-semibold">{entries.length}</span>{' '}
                      {entries.length === 1 ? 'trade' : 'trades'}, effective{' '}
                      <span className="font-semibold">{effectiveDate}</span>.
                      {reason.trim() && <> Reason: <span className="italic">{reason.trim()}</span>.</>}
                    </p>
                    <p className="mt-2 text-sm text-gray-600">
                      ≈Σ Δ daily P&amp;L{' '}
                      <span className={clsx('font-mono font-semibold', deltaAgg.equiv >= 0 ? 'text-emerald-600' : 'text-rose-600')}>
                        {signed(deltaAgg.equiv)} {deltaAgg.base}
                      </span>. Accruals switch to the new rates from the effective date; nothing changes before it.
                    </p>

                    {failed.length > 0 && (
                      <div className="mt-4 rounded-md border border-rose-200 bg-rose-50 p-3">
                        <div className="flex items-center gap-1.5 text-sm font-semibold text-rose-700">
                          <ExclamationTriangleIcon className="h-4 w-4" />
                          Nothing was applied — {failed.length} row(s) failed validation, so the whole batch was rolled back.
                        </div>
                        <ul className="mt-2 space-y-0.5 text-sm text-rose-700">
                          {failed.map((f) => (
                            <li key={f.position_id}>
                              <span className="font-mono">#{f.position_id}</span> — {f.reason}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Any non-failed[] error (payload 400 like over-cap/duplicate, a
                        typed backdated date, 500, or a network drop) — the hook stays
                        quiet by design so the failed[] panel owns business 400s, so
                        surface everything else here or the button dies silently. */}
                    {bulkReRate.isError && failed.length === 0 && (
                      <div className="mt-4 rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700" role="alert">
                        {getErrorMessage(bulkReRate.error, 'Bulk re-rate failed. Please try again.')}
                      </div>
                    )}

                    {blockReason && (
                      <p className="mt-4 text-sm text-amber-700" role="alert">
                        Cannot apply — {blockReason}. Go Back to fix it.
                      </p>
                    )}

                    <div className="mt-5 flex justify-end gap-2">
                      <Button variant="secondary" disabled={bulkReRate.isPending} onClick={() => setStep('staging')}>
                        Back
                      </Button>
                      <Button variant="primary" disabled={!canApply} title={blockReason ?? undefined} onClick={handleConfirm}>
                        {bulkReRate.isPending ? 'Applying…' : `Confirm re-rate (${entries.length})`}
                      </Button>
                    </div>
                  </div>
                )}
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

/** Marks the rows behind the footer's low-rate count. Soft warning, not a gate —
 *  the batch still applies (mirrors the single modal's non-blocking note). */
const LowRateTag: React.FC = () => (
  <span
    className="whitespace-nowrap rounded bg-yellow-100 px-1 py-0.5 text-[10px] font-semibold uppercase tracking-[0.4px] text-yellow-800"
    title={`Resulting rate is below ${LOW_RATE_WARN_PCT}% — verify before applying`}
  >
    rate &lt; {LOW_RATE_WARN_PCT}%
  </span>
);

/** Marks the rows behind the footer's fee-floor count so they can be found and fixed. */
const FeeFloorTag: React.FC = () => (
  <span
    className="whitespace-nowrap rounded bg-amber-100 px-1 py-0.5 text-[10px] font-semibold uppercase tracking-[0.4px] text-amber-700"
    title="Fee must stay above 0% — the batch is rejected as a whole"
  >
    fee ≤ 0
  </span>
);

/**
 * LR-652: the benchmark a fixing-driven rebate row may look at but not set.
 *
 * LR-628 withheld the INPUT — the nightly accrual owns that number and the
 * backend refuses a hand-set one, which in bulk fails the whole batch — but the
 * grid then dropped the FIGURE with it, leaving the spread beside it sitting over
 * nothing and reading as a lost field. That is the symptom LR-652 was filed on.
 *
 * Captioned, and captioned VISIBLY. `rate` falls back to the BOOKED benchmark
 * whenever no fixing resolved (feed down, or before the morning collection), and
 * a booking snapshot on a six-month trade can sit hundreds of bp off market — so
 * an unlabelled figure would quietly swap the level the trader prices against, at
 * the moment they pick one spread for 200 rows. A `title` cannot carry that: a
 * <span> takes no focus, so the tooltip is mouse-only, and the sr-only twin
 * reaches assistive tech alone. Both sibling surfaces (ReRateModal,
 * TradeTermsTab) show the provenance on screen; this is the same affordance at
 * grid density.
 *
 * The caption says "fixing", NOT "today's": `latest_fixings` carries a print
 * forward for up to MAX_FIXING_STALENESS_DAYS and drops its `as_of`, so nothing
 * in the payload can support a same-day claim.
 */
const ResolvedBenchmarkCell: React.FC<{
  tradeId: number;
  rate: number | null | undefined;
  fromFixing: boolean;
}> = ({ tradeId, rate, fromFixing }) => (
  <span
    // `border-transparent` keeps the DecimalInput's height and padding, so the
    // spread input keeps its baseline across rows that do and do not show one.
    // Width is NOT matched: `w-20` is dropped because the figure plus the chip
    // overflows 5rem, so the spread starts a few px further right here than on a
    // row with an input — still far closer than the empty slot it replaced.
    className="shrink-0 whitespace-nowrap border border-transparent px-1.5 py-0.5 font-mono text-sm text-gray-500"
    title={`${
      // The cell is quoted to 2dp like every rate in the grid; the exact booked
      // value has nowhere else to appear, and SONIA/ESTR publish below a bp.
      rate != null && Number.isFinite(rate) ? `${formatDecimalString(rate, { dp: 4 })}% · ` : ''
    }${
      fromFixing
        ? 'resolved by the nightly accrual from the latest published fixing'
        : 'booked benchmark — the accrual found no usable fixing'
    } — re-rate moves the spread`}
  >
    {rateStr(rate)}
    {/* No colour of its own: the chip inherits the cell's `text-gray-500`
        (4.83:1 on white, 4.63:1 on the excluded row's bg-gray-50), exactly as
        the single-modal twin inherits `text-lr-muted`. At 10px it never
        qualifies as large text, so a lighter grey would sit below AA. */}
    <span className="ml-1 font-sans text-[10px] uppercase tracking-[0.4px]">
      {fromFixing ? 'fixing' : 'booked'}
    </span>
    <span className="sr-only">{` benchmark for trade ${tradeId}, not editable`}</span>
  </span>
);

interface BulkRowProps {
  trade: TradeItem;
  ineligibleReason: string | null;
  excluded: boolean;
  mode: 'set_per_row' | 'adjust_bps';
  adjustBps: number | null;
  override: BulkRateOverride | undefined;
  onToggle: (id: number) => void;
  onField: (id: number, field: keyof BulkRateOverride, value: number | undefined) => void;
}

// React.memo: the modal re-renders on every store change (it aggregates the ≈Σ
// footer over all rows), but a memoised row with stable-ref props re-renders only
// when its own slice (override / excluded) or a batch control (mode / adjustBps)
// changes — a keystroke touches one row, not the whole 200-row grid. onToggle /
// onField are the stable store actions; the row binds its own id.
const BulkRow = React.memo<BulkRowProps>(function BulkRow({
  trade, ineligibleReason, excluded, mode, adjustBps, override, onToggle, onField,
}) {
  const editable = reRateEditableFields(trade);
  // LR-652: a rebate row whose benchmark the nightly accrual owns. Keyed on
  // OWNERSHIP rather than on "the allow-list withheld the input", because the cell
  // goes on to caption the figure as the fixing's — the negative space of an input
  // is not evidence of where a number came from. Mode still gates it: a pooled fee
  // trade also reports a benchmark it does not own (LR-631), but that benchmark is
  // its cost leg, not a term of the rate this grid re-rates.
  const showsResolvedBenchmark = reRateMode(trade) === 'rebate' && benchmarkFollowsFixing(trade);
  const resolved = resolveRowValues(trade, mode, adjustBps, override);
  // Per-row marker for the footer's fee-floor tally: a count alone is unactionable
  // in a 200-row batch — the New value column shows total_rate (fee − benchmark)
  // for a fee row, not the fee, so the offender is otherwise invisible.
  const feeFloor = breachesFeeFloor(trade, resolved);
  // LR-628: the "Current" column quotes what the row accrues today, matching the
  // New value beside it (computeNewTotalRate builds on the same effective rate).
  const { totalRate: liveTotalRate, benchmarkRate: liveBenchmark, showingLiveRate } = effectiveTradeRates(trade);
  const oldTotal = typeof liveTotalRate === 'number' ? liveTotalRate : Number(liveTotalRate);
  // Same distrust as the line above, minus its `Number(null) === 0` trap: decimal
  // columns are TYPED number but arrive as strings on some payloads (a recurring
  // LR gotcha, written down in rerate-mapper), and `rateStr` screens on
  // Number.isFinite — so an uncoerced string would print an em dash beside a
  // Current column quoting that very rate.
  const liveBenchmarkNum =
    typeof liveBenchmark === 'number' || liveBenchmark == null ? liveBenchmark : Number(liveBenchmark);
  const newTotal = computeNewTotalRate(trade, resolved);
  // Gate the low-rate marker on the row actually moving, exactly as the footer
  // tally does — an untouched book already below the threshold is not a typo.
  const lowRate = buildBulkEntry(trade, resolved) !== null && warnsLowResultingRate(trade, newTotal);
  const deltaRate = newTotal !== undefined && Number.isFinite(oldTotal) ? newTotal - oldTotal : undefined;
  const deltaDaily = rowDailyDelta(trade, newTotal);
  const disabled = ineligibleReason !== null || excluded;

  return (
    <tr className={clsx('border-b border-gray-100', disabled ? 'bg-gray-50 text-gray-400' : 'hover:bg-blue-50/40')}>
      <td className={TD}>
        <input
          type="checkbox"
          checked={!excluded && !ineligibleReason}
          disabled={ineligibleReason !== null}
          onChange={() => onToggle(trade.id)}
          aria-label={`Include trade ${trade.id}`}
        />
      </td>
      <td className={clsx(TD, 'font-mono')}>#{trade.id}</td>
      <td className={TD}>{trade.symbol_id}</td>
      <td className={TD}>{trade.counterparty}</td>
      <td className={clsx(TD, 'font-mono')}>{rateStr(oldTotal)}</td>
      <td className={TD}>
        {ineligibleReason ? (
          <span className="text-xs italic">{ineligibleReason}</span>
        ) : mode === 'adjust_bps' ? (
          <span className="flex items-center gap-1.5">
            <span className="font-mono text-gray-600">{rateStr(newTotal)}</span>
            {feeFloor && !excluded && <FeeFloorTag />}
            {lowRate && !excluded && <LowRateTag />}
          </span>
        ) : (
          <div className="flex items-center gap-1.5">
            {editable.includes('benchmark_rate') ? (
              <DecimalInput
                value={override?.benchmark_rate}
                onChange={(v) => onField(trade.id, 'benchmark_rate', v)}
                precision={4}
                allowNegative
                disabled={excluded}
                className="w-20 rounded border border-gray-300 px-1.5 py-0.5 text-sm focus:border-blue-500 focus:outline-none disabled:bg-gray-100"
                placeholder={formatDecimalString(Number(trade.benchmark_rate ?? 0), { dp: 2 })}
                aria-label={`New benchmark for trade ${trade.id}`}
              />
            ) : showsResolvedBenchmark ? (
              <ResolvedBenchmarkCell
                tradeId={trade.id}
                rate={liveBenchmarkNum}
                fromFixing={showingLiveRate}
              />
            ) : null}
            {editable.includes('spread_bps') && (
              <DecimalInput
                value={override?.spread_bps}
                onChange={(v) => onField(trade.id, 'spread_bps', v)}
                precision={0}
                allowNegative
                disabled={excluded}
                className="w-16 rounded border border-gray-300 px-1.5 py-0.5 text-sm focus:border-blue-500 focus:outline-none disabled:bg-gray-100"
                placeholder={`${trade.spread_bps ?? 0}bps`}
                aria-label={`New spread for trade ${trade.id}`}
              />
            )}
            {editable.includes('fee_percent') && (
              <DecimalInput
                value={override?.fee_percent}
                onChange={(v) => onField(trade.id, 'fee_percent', v)}
                precision={4}
                disabled={excluded}
                className="w-20 rounded border border-gray-300 px-1.5 py-0.5 text-sm focus:border-blue-500 focus:outline-none disabled:bg-gray-100"
                placeholder={formatDecimalString(Number(trade.fee_percent ?? 0), { dp: 2 })}
                aria-label={`New fee for trade ${trade.id}`}
              />
            )}
            {feeFloor && !excluded && <FeeFloorTag />}
            {lowRate && !excluded && <LowRateTag />}
          </div>
        )}
      </td>
      <td className={clsx(TD, 'text-right font-mono')}>
        {disabled || deltaRate === undefined || deltaRate === 0 ? '—' : `${signed(deltaRate)}%`}
      </td>
      <td className={clsx(TD, 'text-right font-mono')}>
        {disabled || deltaDaily === null || deltaDaily === 0
          ? '—'
          : formatCurrencyExact(deltaDaily, trade.currency)}
      </td>
    </tr>
  );
});
